/**
 * Settings Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useSettings hook.
 */

import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import {
  useSettings,
  ProfileTab,
  NotificationsTab,
  SecurityTab,
  APIAccessTab,
} from './settings-page';

// CR-5370: Company tab removed from My Account — company configuration
// lives in the admin/module areas. CR-5369: also removed the Login As
// section from the Security tab + the supporting imports/destructured
// values (isHelixAdmin / startImpersonation / useIsCustomer were only
// referenced from those removed surfaces).

export default function Settings() {
  const { user } = useAuth();
  // External-API token management is sensitive: only company admins or
  // platform superusers can mint / rotate / revoke. Both flags are typed on
  // the User interface in `lib/api/types/common.ts`.
  const canManageApiTokens =
    user?.is_company_admin === true || user?.is_superuser === true;
  const {
    // Profile
    profileForm,
    loadingProfile,
    savingProfile,
    handleProfileChange,
    handleSaveProfile,

    // Notifications
    notifPrefs,
    loadingNotifPrefs,
    savingNotifPrefs,
    handleNotifPrefChange,
  } = useSettings();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">My Account</h1>
        <p className="text-muted-foreground mt-2">
          Manage your personal preferences and account settings.
        </p>
      </div>

      {/* Settings Tabs */}
      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          {canManageApiTokens && <TabsTrigger value="api">API Access</TabsTrigger>}
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <ProfileTab
            profileForm={profileForm}
            loadingProfile={loadingProfile}
            savingProfile={savingProfile}
            onProfileChange={handleProfileChange}
            onSaveProfile={handleSaveProfile}
          />
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4">
          <NotificationsTab
            notifPrefs={notifPrefs}
            loadingNotifPrefs={loadingNotifPrefs}
            savingNotifPrefs={savingNotifPrefs}
            onPrefChange={handleNotifPrefChange}
          />
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-4">
          <SecurityTab />
        </TabsContent>

        {/* API Access Tab — admin / superuser only */}
        {canManageApiTokens && (
          <TabsContent value="api" className="space-y-4">
            <APIAccessTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
